(() => {
  class NativeCollectionSlideshow {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('.native-slideshow__viewport');
      this.slides = [...section.querySelectorAll('.native-slideshow__slide')];
      this.bullets = [...section.querySelectorAll('.native-slideshow__pagination-bullet')];
      this.controls = section.querySelector('.native-slideshow__controls');
      this.prevButton = section.querySelector('.native-slideshow__nav-button--prev');
      this.nextButton = section.querySelector('.native-slideshow__nav-button--next');
      this.currentIndex = 0;
      this.animationFrame = null;
      this.textTimer = null;
      this.lockedUntil = 0;
      this.lastWheelAt = 0;
      this.wheelConsumed = false;
      this.touchStartY = null;
      this.touchStartedPinned = false;
      this.touchHandled = false;
      this.abortController = new AbortController();

      if (!this.viewport || !this.slides.length) return;
      this.setupImages();
      this.setActive(0, false);

      const signal = this.abortController.signal;
      this.bullets.forEach((bullet) => {
        bullet.addEventListener('click', () => this.goTo(Number(bullet.dataset.index)), { signal });
      });
      this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1), { signal });
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1), { signal });
      window.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
      window.addEventListener('resize', () => {
        this.setupImages();
        this.scheduleUpdate();
      }, { passive: true, signal });
      window.addEventListener('wheel', (event) => this.onWheel(event), { passive: false, capture: true, signal });
      window.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true, signal });
      window.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false, signal });
      window.addEventListener('touchend', () => this.onTouchEnd(), { passive: true, signal });
      window.addEventListener('touchcancel', () => this.onTouchEnd(), { passive: true, signal });
      window.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
      this.update();
    }

    destroy() {
      this.abortController.abort();
      if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);
      if (this.textTimer !== null) window.clearTimeout(this.textTimer);
    }

    setupImages() {
      const screenWidth = window.innerWidth;
      const isRetina = window.devicePixelRatio > 1;
      this.slides.forEach((slide) => {
        const { bgMobile, bgTablet, bgDesktop, bgRetina } = slide.dataset;
        if (!bgMobile) return;
        const image = screenWidth <= 767
          ? bgMobile
          : screenWidth <= 1024
            ? bgTablet || bgDesktop
            : bgDesktop;
        slide.style.backgroundImage = `url('${isRetina && bgRetina ? bgRetina : image}')`;
      });
    }

    sectionTop() {
      return this.section.getBoundingClientRect().top + window.scrollY;
    }

    stepHeight() {
      return this.viewport.getBoundingClientRect().height;
    }

    isPinned() {
      const viewportRect = this.viewport.getBoundingClientRect();
      const sectionRect = this.section.getBoundingClientRect();
      return Math.abs(viewportRect.top) <= 2 &&
        sectionRect.top <= 2 &&
        sectionRect.bottom >= viewportRect.height - 2;
    }

    isInOverlay(target) {
      return target instanceof Element &&
        Boolean(target.closest('dialog, [role="dialog"], [aria-modal="true"], .cart-drawer, .drawer'));
    }

    setActive(index, animateText = true) {
      if (index < 0 || index >= this.slides.length) return;
      if (this.textTimer !== null) window.clearTimeout(this.textTimer);
      this.currentIndex = index;
      this.slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-before', slideIndex < index);
        slide.classList.toggle('is-current', slideIndex === index);
        slide.classList.toggle('is-after', slideIndex > index);
        slide.inert = slideIndex !== index;
        slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
      });
      this.bullets.forEach((bullet, bulletIndex) => {
        const active = bulletIndex === index;
        bullet.classList.toggle('is-active', active);
        bullet.setAttribute('aria-current', active ? 'true' : 'false');
      });
      this.updateButton(this.prevButton, index === 0);
      this.updateButton(this.nextButton, index === this.slides.length - 1);

      // The hold attribute belongs only to text that has never been revealed.
      // Once refreshAnimation removes it, revisiting the slide keeps the text visible.
      if (animateText) {
        const motionElements = this.slides[index].querySelectorAll('motion-element[hold]');
        if (!motionElements.length) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.FoxTheme?.config?.motionReduced) {
          motionElements.forEach((element) => element.removeAttribute('hold'));
        } else {
          this.textTimer = window.setTimeout(() => {
            if (this.currentIndex !== index) return;
            motionElements.forEach((element) => {
              if (typeof element.refreshAnimation === 'function') element.refreshAnimation();
              else element.removeAttribute('hold');
            });
            this.textTimer = null;
          }, 270);
        }
      }
    }

    updateButton(button, disabled) {
      if (!button) return;
      button.classList.toggle('is-disabled', disabled);
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    scheduleUpdate() {
      if (this.animationFrame !== null) return;
      this.animationFrame = requestAnimationFrame(() => {
        this.animationFrame = null;
        this.update();
      });
    }

    update() {
      const sectionRect = this.section.getBoundingClientRect();
      const viewportMiddle = window.innerHeight / 2;
      const visible = sectionRect.top < viewportMiddle && sectionRect.bottom > viewportMiddle;
      this.controls?.classList.toggle('is-section-in-view', visible);
      if (!visible) return;

      const step = this.stepHeight();
      if (step <= 0) return;
      const progress = (window.scrollY - this.sectionTop()) / step;
      const index = Math.max(0, Math.min(this.slides.length - 1, Math.round(progress)));
      if (index !== this.currentIndex) this.setActive(index);
    }

    goTo(index) {
      if (index < 0 || index >= this.slides.length) return false;
      this.setActive(index);
      window.scrollTo({ top: this.sectionTop() + index * this.stepHeight(), behavior: 'instant' });
      this.lockedUntil = performance.now() + 700;
      return true;
    }

    onWheel(event) {
      if (event.defaultPrevented || event.ctrlKey || this.isInOverlay(event.target)) return;
      const amount = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1);
      if (!amount) return;
      const direction = Math.sign(amount);
      const now = performance.now();
      if (now - this.lastWheelAt > 240) this.wheelConsumed = false;
      this.lastWheelAt = now;

      const top = this.sectionTop();
      const lastAnchor = top + (this.slides.length - 1) * this.stepHeight();
      if (!this.isPinned()) {
        if (now < this.lockedUntil && this.wheelConsumed) {
          event.preventDefault();
          return;
        }
        const enteringFromAbove = direction > 0 && window.scrollY < top && window.scrollY + amount >= top;
        const enteringFromBelow = direction < 0 && window.scrollY > lastAnchor && window.scrollY + amount <= lastAnchor;
        if (enteringFromAbove || enteringFromBelow) {
          event.preventDefault();
          this.setActive(enteringFromAbove ? 0 : this.slides.length - 1);
          window.scrollTo({ top: enteringFromAbove ? top : lastAnchor, behavior: 'smooth' });
          this.lockedUntil = now + 600;
          this.wheelConsumed = true;
        }
        return;
      }

      if (now < this.lockedUntil) {
        event.preventDefault();
        return;
      }
      if (this.goTo(this.currentIndex + direction)) {
        event.preventDefault();
        this.wheelConsumed = true;
      }
      // At either end, the browser scrolls the surrounding page normally.
    }

    onTouchStart(event) {
      if (this.isInOverlay(event.target)) return;
      this.touchStartY = event.touches[0]?.clientY ?? null;
      this.touchStartedPinned = this.isPinned();
      this.touchHandled = false;
    }

    onTouchMove(event) {
      if (this.touchStartY === null || !this.touchStartedPinned) return;
      if (this.touchHandled) {
        event.preventDefault();
        return;
      }
      const distance = this.touchStartY - (event.touches[0]?.clientY ?? this.touchStartY);
      const nextIndex = this.currentIndex + Math.sign(distance);
      if (nextIndex < 0 || nextIndex >= this.slides.length) {
        this.touchStartedPinned = false;
        return;
      }
      // Cancel the native scroll from the first movement, before touch inertia starts.
      event.preventDefault();
      if (Math.abs(distance) < 45) return;
      this.goTo(nextIndex);
      this.touchHandled = true;
    }

    onTouchEnd() {
      this.touchStartY = null;
      this.touchStartedPinned = false;
      this.touchHandled = false;
    }

    onKeyDown(event) {
      if (!this.isPinned() || event.defaultPrevented || event.ctrlKey || event.altKey || event.metaKey) return;
      if (event.target instanceof Element && event.target.closest('a, button, input, textarea, select, [contenteditable="true"]')) return;
      const direction = event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === ' ' && !event.shiftKey
        ? 1
        : event.key === 'ArrowUp' || event.key === 'PageUp' || event.key === ' ' && event.shiftKey
          ? -1
          : 0;
      if (direction && this.goTo(this.currentIndex + direction)) event.preventDefault();
    }
  }

  const nativeCollectionSlideshows = new WeakMap();

  function initializeNativeCollectionSlideshows(root = document) {
    const sections = root.matches?.('[data-native-slideshow]')
      ? [root]
      : root.querySelectorAll('[data-native-slideshow]');
    sections.forEach((section) => {
      if (nativeCollectionSlideshows.has(section)) return;
      nativeCollectionSlideshows.set(section, new NativeCollectionSlideshow(section));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeNativeCollectionSlideshows(), { once: true });
  } else {
    initializeNativeCollectionSlideshows();
  }

  document.addEventListener('shopify:section:load', (event) => initializeNativeCollectionSlideshows(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const sections = event.target.matches?.('[data-native-slideshow]')
      ? [event.target]
      : event.target.querySelectorAll('[data-native-slideshow]');
    sections.forEach((section) => {
      nativeCollectionSlideshows.get(section)?.destroy();
      nativeCollectionSlideshows.delete(section);
    });
  });
})();
